import JSZip from 'jszip';
import { getTranscriptById, getTranscriptUrl } from '../modules/api/zoom';
import { logger, LogCategory } from '../services/logger';

interface MeetingWithTranscript {
    id: string;
    topic: string;
    startTime: string;
    hostName?: string;
}

interface DownloadProgress {
    current: number;
    total: number;
    currentMeeting: string;
}

/**
 * Parse VTT content and convert to formatted text
 */
function parseVTTToText(vttContent: string, meetingTitle: string): string {
    const lines = vttContent.split('\n');
    const segments: Array<{ time: string; text: string; speaker?: string }> = [];
    
    let currentTime = '';
    let isInSegment = false;
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip WEBVTT header and empty lines
        if (line === 'WEBVTT' || line === '' || /^\d+$/.test(line)) continue;
        
        // Check for timestamp line
        if (line.includes('-->')) {
            const [startTime] = line.split('-->').map(t => t.trim());
            currentTime = formatVTTTime(startTime);
            isInSegment = true;
        } else if (isInSegment && line) {
            // Parse speaker if present
            const speakerMatch = line.match(/^([^:]+):\s*(.+)$/);
            if (speakerMatch) {
                segments.push({
                    time: currentTime,
                    speaker: speakerMatch[1],
                    text: speakerMatch[2]
                });
            } else {
                segments.push({
                    time: currentTime,
                    text: line
                });
            }
            isInSegment = false;
        }
    }
    
    // Format the output
    const header = `Meeting Transcript: ${meetingTitle}\n`;
    const dateHeader = `Generated: ${new Date().toLocaleString()}\n`;
    const separator = '='.repeat(60) + '\n\n';
    
    const content = segments.map(segment => {
        const speaker = segment.speaker ? `${segment.speaker}: ` : '';
        return `[${segment.time}] ${speaker}${segment.text}`;
    }).join('\n\n');
    
    return header + dateHeader + separator + content;
}

/**
 * Format VTT timestamp to readable format
 */
function formatVTTTime(timeStr: string): string {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
        const [hours, minutes, seconds] = parts;
        const h = parseInt(hours);
        const m = parseInt(minutes);
        const s = Math.floor(parseFloat(seconds));
        
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        } else {
            return `${m}:${s.toString().padStart(2, '0')}`;
        }
    }
    return timeStr;
}

/**
 * Create a safe filename from meeting topic, date, and host name
 */
function createSafeFilename(topic: string, startTime: string, hostName?: string): string {
    // Parse the date
    const date = new Date(startTime);
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Clean the host name if provided
    const cleanHostName = hostName
        ? hostName
            .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .slice(0, 30) // Limit length
        : '';
    
    // Clean the topic
    const cleanTopic = topic
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .slice(0, 50); // Limit length
    
    // Build filename with host name as prefix if available
    if (cleanHostName) {
        return `${cleanHostName}_${dateStr}_${cleanTopic}.txt`;
    }
    return `${dateStr}_${cleanTopic}.txt`;
}

/**
 * Download all transcripts for given meetings and create a zip file
 */
export async function downloadBulkTranscripts(
    meetings: MeetingWithTranscript[],
    userName?: string,
    onProgress?: (progress: DownloadProgress) => void
): Promise<void> {
    const startTime = performance.now();
    
    logger.info(
        LogCategory.TRANSCRIPT_USER_ACTION,
        'BULK_DOWNLOAD_INITIATED',
        `Starting bulk download of ${meetings.length} transcripts`,
        { meetingCount: meetings.length }
    );
    
    const zip = new JSZip();
    const errors: Array<{ meeting: string; error: string }> = [];
    let successCount = 0;
    
    for (let i = 0; i < meetings.length; i++) {
        const meeting = meetings[i];
        
        if (onProgress) {
            onProgress({
                current: i + 1,
                total: meetings.length,
                currentMeeting: meeting.topic
            });
        }
        
        try {
            // Check if transcript exists
            const metadata = await getTranscriptById(meeting.id);
            
            if (!metadata || !metadata.hasTranscript) {
                logger.info(
                    LogCategory.TRANSCRIPT_USER_ACTION,
                    'BULK_DOWNLOAD_SKIP_NO_TRANSCRIPT',
                    `Skipping ${meeting.topic} - no transcript available`,
                    { meetingId: meeting.id }
                );
                continue;
            }
            
            // Get transcript URL
            const urlData = await getTranscriptUrl(meeting.id);
            
            if (!urlData?.signedUrl) {
                throw new Error('No signed URL available');
            }
            
            // Fetch transcript content
            const response = await fetch(urlData.signedUrl);
            const vttContent = await response.text();
            
            // Convert VTT to formatted text
            const textContent = parseVTTToText(vttContent, meeting.topic);
            
            // Add to zip with safe filename including host name
            const filename = createSafeFilename(meeting.topic, meeting.startTime, meeting.hostName);
            zip.file(filename, textContent);
            
            successCount++;
            
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'BULK_DOWNLOAD_FILE_ADDED',
                `Added ${filename} to zip`,
                { 
                    meetingId: meeting.id,
                    filename,
                    contentLength: textContent.length
                }
            );
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            errors.push({ meeting: meeting.topic, error: errorMessage });
            
            logger.error(
                LogCategory.TRANSCRIPT_ERROR,
                'BULK_DOWNLOAD_FILE_ERROR',
                `Failed to download transcript for ${meeting.topic}`,
                error as Error,
                { meetingId: meeting.id }
            );
        }
    }
    
    // Generate the zip file
    const blob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
    });
    
    // Create download filename with user name and current date
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const cleanUserName = userName
        ? userName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').slice(0, 30) + '_'
        : '';
    const filename = `${cleanUserName}transcripts_bulk_${dateStr}.zip`;
    
    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    const duration = performance.now() - startTime;
    
    logger.info(
        LogCategory.TRANSCRIPT_USER_ACTION,
        'BULK_DOWNLOAD_COMPLETED',
        `Bulk download completed in ${duration}ms`,
        {
            duration,
            totalMeetings: meetings.length,
            successCount,
            errorCount: errors.length,
            filename,
            zipSize: blob.size
        }
    );
    
    // If there were errors, log them
    if (errors.length > 0) {
        logger.warn(
            LogCategory.TRANSCRIPT_ERROR,
            'BULK_DOWNLOAD_PARTIAL_SUCCESS',
            `Bulk download completed with ${errors.length} errors`,
            { errors }
        );
    }
}